/* stylelint-disable no-descending-specificity */
import styled, { css } from 'styled-components';

import { menuConsts, horizontalSelectedOffset } from './Menu.consts';
import { IMenuProps } from './Menu.types';

const getEffect = () => `
  animation-duration: 0.3s;
  animation-fill-mode: both;
  transform-origin: 0 0;
`;

const Animations = css<Required<Pick<IMenuProps, 'prefixCls'>>>`
  &-open {
    &-slide-up-enter,
    &-slide-up-appear {
      ${getEffect()}
      opacity: 0;
      animation-timing-function: cubic-bezier(0.08, 0.82, 0.17, 1);
      animation-play-state: paused;
    }

    &-slide-up-leave {
      ${getEffect()}
      opacity: 1;
      animation-timing-function: cubic-bezier(0.6, 0.04, 0.98, 0.34);
      animation-play-state: paused;
    }

    &-slide-up-enter&-slide-up-enter-active,
    &-slide-up-appear&-slide-up-appear-active {
      animation-name: rc-menu-open-slide-up-in;
      animation-play-state: running;
    }

    &-slide-up-leave&-slide-up-leave-active {
      animation-name: rc-menu-open-slide-up-out;
      animation-play-state: running;
    }

    @keyframes rc-menu-open-slide-up-in {
      0% {
        opacity: 0;
        transform-origin: 0 0;
        transform: scaleY(0);
      }

      100% {
        opacity: 1;
        transform-origin: 0 0;
        transform: scaleY(1);
      }
    }

    @keyframes rc-menu-open-slide-up-out {
      0% {
        opacity: 1;
        transform-origin: 0 0;
        transform: scaleY(1);
      }

      100% {
        opacity: 0;
        transform-origin: 0 0;
        transform: scaleY(0);
      }
    }

    &-zoom-enter,
    &-zoom-appear {
      opacity: 0;
      ${getEffect()}
      animation-timing-function: cubic-bezier(0.08, 0.82, 0.17, 1);
      animation-play-state: paused;
    }

    &-zoom-leave {
      ${getEffect()}
      animation-timing-function: cubic-bezier(0.6, 0.04, 0.98, 0.34);
      animation-play-state: paused;
    }

    &-zoom-enter&-zoom-enter-active,
    &-zoom-appear&-zoom-appear-active {
      animation-name: rc-menu-open-zoom-in;
      animation-play-state: running;
    }

    &-zoom-leave&-zoom-leave-active {
      animation-name: rc-menu-open-zoom-out;
      animation-play-state: running;
    }

    &-zoom-enter,
    &-zoom-appear,
    &-zoom-leave {
      .${({ prefixCls }) => prefixCls}-submenu-rtl & {
        transform-origin: top right !important;
      }
    }

    @keyframes rc-menu-open-zoom-in {
      0% {
        opacity: 0;
        transform: scale(0, 0);
      }

      100% {
        opacity: 1;
        transform: scale(1, 1);
      }
    }

    @keyframes rc-menu-open-zoom-out {
      0% {
        transform: scale(1, 1);
      }

      100% {
        opacity: 0;
        transform: scale(0, 0);
      }
    }
  }
`;

const Submenu = css<Required<Pick<IMenuProps, 'prefixCls' | 'mode' | 'size'>>>`
  ${({ prefixCls, theme, mode, size }) => css`
    &-submenu {
      box-sizing: border-box;

      &-open > .${prefixCls}-submenu-title {
        .submenu-chevron {
          transform: rotate(180deg);
        }
      }

      &-popup {
        position: absolute;

        .${prefixCls} .submenu-chevron {
          transform: rotate(-90deg);
        }

        .submenu-title-wrapper {
          padding-right: 20px;

          .${prefixCls}-submenu-rtl &,
          .${prefixCls}-submenu-rtl & {
            padding-right: 0;
            padding-left: 20px;
          }
        }
      }

      &-selected.${prefixCls}-submenu-horizontal > .${prefixCls}-submenu-title {
        border-bottom: ${mode === 'horizontal'
          ? `${horizontalSelectedOffset}px solid ${theme.accent.primary.strong}`
          : 0};
      }

      &-hidden {
        display: none;
      }

      .${prefixCls} {
        .${prefixCls}-item-selected {
          ${mode === 'horizontal'
            ? css`
                border: 0;
              `
            : null};
        }
      }
    }
    &-item,
    &-submenu-title {
      ${(size === 'small' || size === 'medium') && theme.paragraph.small.normal};
      ${size === 'large' && theme.paragraph.large.normal};

      box-sizing: border-box;
      outline: none;
      margin: 0;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: ${mode === 'horizontal' ? `${horizontalSelectedOffset}px solid transparent` : 0};
      min-height: ${menuConsts[size].itemHeight}px;
      padding-left: ${menuConsts[size].leftPadding}px;
      padding-right: ${menuConsts[size].rightPadding}px;
      white-space: nowrap;
      cursor: pointer;

      .${prefixCls}-rtl & {
        padding: 7px 16px 7px 7px;
      }

      &.${prefixCls}-item-disabled, &.${prefixCls}-submenu-disabled {
        cursor: not-allowed;
        color: ${theme.text.onSurface.inactive} !important;
      }

      &-content {
        display: flex;
        align-items: center;
        svg {
          margin-right: ${size === 'large' ? '6px' : '4px'};
          width: ${menuConsts[size].iconSize}px;
          height: ${menuConsts[size].iconSize}px;
        }
      }
      &-flyout {
        height: ${menuConsts[size].iconSize}px;
        width: ${menuConsts[size].iconSize}px;
        transition: transform 0.3s;
      }
    }

    .${prefixCls}-submenu-title > div > .submenu-chevron {
      height: ${menuConsts[size].iconSize}px;
      width: ${menuConsts[size].iconSize}px;
      transition: transform 0.3s;
    }
  `}
`;

export const MenuWrapper = styled.div<Required<Pick<IMenuProps, 'prefixCls' | 'size' | 'mode'>>>`
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  ${({ size, prefixCls, theme, mode }) => css`
    .${prefixCls} {
      ${(size === 'small' || size === 'medium') && theme.paragraph.small.normal};
      ${size === 'large' && theme.paragraph.large.normal};

      background: ${theme.background.surface};
      color: ${theme.text.onSurface.strong};
      outline: none;
      margin: 0;
      padding-left: 0;
      padding-right: 0;
      list-style: none;
      height: 100%;

      ${Submenu}

      &-rtl {
        direction: rtl;
      }

      &-hidden {
        display: none;
      }

      &-collapse {
        overflow: hidden;
        transition: height 0.3s ease-out;
      }

      &-item-group-list {
        margin: 0;
        padding: 0;
      }

      &-item-group-title {
        line-height: 1.5;
        padding: 8px 10px;
        border-bottom: 1px solid ${theme.border.medium};
      }

      &-item-active,
      &-submenu-active > .${prefixCls}-submenu-title {
        background-color: ${theme.focus.subtle};
      }

      & > li.${prefixCls}-submenu {
        padding: 0;
      }

      &-overflow-item {
        flex: none;
        display: flex;
      }

      &-horizontal.${prefixCls}-sub,
        &-vertical.${prefixCls}-sub,
        &-vertical-left.${prefixCls}-sub,
        &-vertical-right.${prefixCls}-sub {
        min-width: 160px;
        margin-top: 0;
      }

      &-item-selected {
        transform: translateZ(0);
        border-right: ${mode === 'horizontal'
          ? 0
          : `${horizontalSelectedOffset}px solid ${theme.accent.primary.strong}`};
        border-bottom: ${mode === 'horizontal'
          ? `${horizontalSelectedOffset}px solid ${theme.accent.primary.strong}`
          : 0};
        padding-right: ${mode === 'horizontal'
          ? 'initial'
          : menuConsts[size].rightPadding - horizontalSelectedOffset}px;
      }

      & > .${prefixCls}-item-divider {
        height: 1px;
        margin: 1px 0;
        overflow: hidden;
        padding: 0;
        line-height: 0;
        background-color: ${theme.border.subtle};
      }

      &-inline {
        .${prefixCls} {
          background-color: ${theme.background.base};
        }
      }

      &-horizontal {
        display: flex;
        flex-wrap: nowrap;
        width: 100%;
        border: none;
        box-shadow: none;
        white-space: nowrap;

        .${prefixCls}-submenu-title svg {
          margin-left: 4px;
        }
      }

      &-horizontal > .${prefixCls}-item, &-horizontal > .${prefixCls}-submenu > .${prefixCls}-submenu-title {
        padding: 0 8px;
      }

      &-vertical,
      &-vertical-left,
      &-vertical-right,
      &-inline {
        .${prefixCls}-submenu-arrow {
          display: none;
        }
      }

      &-inline {
        .${prefixCls}-submenu-arrow {
          transform: rotate(90deg);
          transition: transform 0.3s;
        }
        & .${prefixCls}-submenu-open > .${prefixCls}-submenu-title {
          .${prefixCls}-submenu-arrow {
            transform: rotate(-90deg);
          }
        }
      }

      &-vertical.${prefixCls}-sub, &-vertical-left.${prefixCls}-sub, &-vertical-right.${prefixCls}-sub {
        padding: 0;

        .${prefixCls}-submenu-rtl & {
          direction: rtl;
        }
      }

      &-sub.${prefixCls}-inline {
        padding: 0;
        border: none;
        border-radius: 0;
        box-shadow: none;

        & > .${prefixCls}-item, & > .${prefixCls}-submenu > .${prefixCls}-submenu-title {
          .${prefixCls}-rtl & {
            padding-left: 0;
          }
        }
      }

      &-item-group-title {
        ${theme.label.extraSmall.normal}
        color: ${theme.text.onSurface.subtle};
        padding-left: 14px;
      }

      &-sub {
        border-radius: 2px;

        .${prefixCls}-item-group-title {
          padding-left: 28px;
        }
      }

      &-sub:not(.${prefixCls}-inline) {
        .${prefixCls}-submenu-selected {
          background-color: ${theme.focus.subtle};
        }
      }

      ${Animations}
    }
  `}
`;
